export const AUDIT_LOG_ACTION = {
    SAVED: 'SAVED',
    EDITED: 'EDITED',
    UPDATED: 'UPDATED',
    VOIDED: 'VOIDED',
    RESUME: 'RESUME',
    COMPLETED: 'COMPLETED',
    CREATED: 'CREATED',
} as const;

export type AuditLogAction =
    (typeof AUDIT_LOG_ACTION)[keyof typeof AUDIT_LOG_ACTION];

export const AUDIT_LOG_CART_UPDATE_TYPE = {
    ADD_ITEMS: 'ADD_ITEMS',
    REMOVE_ITEMS: 'REMOVE_ITEMS',
    UPDATE_QUANTITIES: 'UPDATE_QUANTITIES',
    ADD_ITEM_NOTE: 'ADD_ITEM_NOTE',
    MODIFIER_CHANGED: 'MODIFIER_CHANGED',
} as const;

export type AuditLogCartUpdateType =
    (typeof AUDIT_LOG_CART_UPDATE_TYPE)[keyof typeof AUDIT_LOG_CART_UPDATE_TYPE];

export const AUDIT_LOG_MODIFIER_CHANGE_TYPE = {
    ADDED: 'ADDED',
    REMOVED: 'REMOVED',
    QUANTITY_UPDATED: 'QUANTITY_UPDATED',
} as const;

export type AuditLogModifierChangeType =
    (typeof AUDIT_LOG_MODIFIER_CHANGE_TYPE)[keyof typeof AUDIT_LOG_MODIFIER_CHANGE_TYPE];

export const AUDIT_LOG_EDITED_FIELD = {
    NAME: 'NAME',
    COMMENT: 'COMMENT',
    CUSTOMER_NAME: 'CUSTOMER_NAME',
    ORDER_NOTE: 'ORDER_NOTE',
} as const;

export type AuditLogEditedField =
    (typeof AUDIT_LOG_EDITED_FIELD)[keyof typeof AUDIT_LOG_EDITED_FIELD];

type AuditLogPlatform = 'WEB';

export interface AuditLogBase {
    id: string;
    staffId: string;
    staffName: string;
    authorizedById: string | null;
    authorizedByName: string | null;
    timestamp: string;
    deviceId: string;
    platform: AuditLogPlatform;
}

export interface AuditLogItemRef {
    itemId: string;
    itemName: string;
    variantId: string;
    variantName: string;
}

export interface AuditLogCreatedAction extends AuditLogBase {
    action: typeof AUDIT_LOG_ACTION.CREATED;
}

export interface AuditLogSavedAction extends AuditLogBase {
    action: typeof AUDIT_LOG_ACTION.SAVED;
}

export interface AuditLogEditedAction extends AuditLogBase {
    action: typeof AUDIT_LOG_ACTION.EDITED;
    changedFields: {
        field: AuditLogEditedField;
        previousValue: string;
        currentValue: string;
    }[];
}

export interface AuditLogVoidedAction extends AuditLogBase {
    action: typeof AUDIT_LOG_ACTION.VOIDED;
    reason: string;
}

export interface AuditLogResumeAction extends AuditLogBase {
    action: typeof AUDIT_LOG_ACTION.RESUME;
}

export interface AuditLogCompletedAction extends AuditLogBase {
    action: typeof AUDIT_LOG_ACTION.COMPLETED;
}

interface AuditLogUpdatedBase extends AuditLogBase {
    action: typeof AUDIT_LOG_ACTION.UPDATED;
}

export interface AuditLogAddItemsAction extends AuditLogUpdatedBase {
    cartUpdateType: typeof AUDIT_LOG_CART_UPDATE_TYPE.ADD_ITEMS;
    itemsAdded: (AuditLogItemRef & { quantity: number; unit: string })[];
}

export interface AuditLogRemoveItemsAction extends AuditLogUpdatedBase {
    cartUpdateType: typeof AUDIT_LOG_CART_UPDATE_TYPE.REMOVE_ITEMS;
    itemsRemoved: AuditLogItemRef[];
}

export interface AuditLogUpdateQuantitiesAction extends AuditLogUpdatedBase {
    cartUpdateType: typeof AUDIT_LOG_CART_UPDATE_TYPE.UPDATE_QUANTITIES;
    quantitiesChanged: (AuditLogItemRef & {
        oldQty: number;
        newQty: number;
        unit: string;
    })[];
}

export interface AuditLogAddNoteAction extends AuditLogUpdatedBase {
    cartUpdateType: typeof AUDIT_LOG_CART_UPDATE_TYPE.ADD_ITEM_NOTE;
    itemNoteChanges: (AuditLogItemRef & {
        previousNote: string;
        currentNote: string;
    })[];
}

export interface AuditLogUpdateModifiersAction extends AuditLogUpdatedBase {
    cartUpdateType: typeof AUDIT_LOG_CART_UPDATE_TYPE.MODIFIER_CHANGED;
    modifiersChanged: {
        cartItemId: string;
        itemId: string;
        itemName: string;
        variantId: string;
        variantName: string;
        modifiers: {
            changeType: AuditLogModifierChangeType;
            modifierGroupId: string;
            modifierGroupName: string;
            modifierOptionId: string;
            modifierOptionName: string;
            oldQty: number;
            newQty: number;
        }[];
    }[];
}

export type AuditLogUpdatedAction =
    | AuditLogAddItemsAction
    | AuditLogRemoveItemsAction
    | AuditLogUpdateQuantitiesAction
    | AuditLogAddNoteAction
    | AuditLogUpdateModifiersAction;

export type AuditLog =
    | AuditLogCreatedAction
    | AuditLogSavedAction
    | AuditLogEditedAction
    | AuditLogVoidedAction
    | AuditLogResumeAction
    | AuditLogCompletedAction
    | AuditLogUpdatedAction;
